import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useAsu } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";

import type { IChartData } from "../../scheme/components/analytics/interfaces";
import type { IStructureTreePoint, MULTI_ANALYSIS_MEASURE_TYPE } from "../types";
import { mapMultiAnalysisMeasureTypeToAnalytics } from "../utils/measure-type-map";
import { fetchMultiAnalysisMeasureOptions, fetchMultiAnalysisTrendMeasureData } from "../utils/measure-options";
import { normalizeMultiAnalysisTrendSeriesDates } from "../utils/range-frame";
import {
  buildMultiAnalysisTrendSeries,
  filterMeasureResponseByCellFilters,
  resolveMultiAnalysisTrendMeasureOptions,
  normalizeTrendRangeForRequest,
  type IMultiAnalysisRequestTimeRange,
  type IMultiAnalysisTimeRange,
} from "../utils/trend-series";

interface IUseMultiAnalysisTrendSeriesArgs {
  enabled: boolean;
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisTimeRange;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE | null;
  cellFilters?: Record<string, unknown>;
}

interface ILoadTrendSeriesArgs {
  points: readonly IStructureTreePoint[];
  range: IMultiAnalysisRequestTimeRange;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE;
  cellFilters: Record<string, unknown>;
  dbTz: number;
}

export const useMultiAnalysisTrendSeries = ({
  enabled,
  points,
  range,
  measureType,
  cellFilters = {},
}: IUseMultiAnalysisTrendSeriesArgs): { series: IChartData[]; isLoading: boolean } => {
  const { dbTz } = useAsu();
  const { formatMessage } = useIntl();
  const [series, setSeries] = useState<IChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { fn: loadTrendSeries } = useApiCall(
    async (api, args: ILoadTrendSeriesArgs): Promise<IChartData[]> => {
      const pointIds = args.points.map(({ id }) => id);
      const analyticsMeasureType = mapMultiAnalysisMeasureTypeToAnalytics(args.measureType);

      const measureOptions = resolveMultiAnalysisTrendMeasureOptions(
        await fetchMultiAnalysisMeasureOptions(api, analyticsMeasureType, pointIds),
        args.measureType,
        args.cellFilters,
      );
      const measureNames = Array.from(new Set(measureOptions.map(({ title }) => title)));

      if (!measureNames.length) {
        return [];
      }

      const response = filterMeasureResponseByCellFilters(
        await fetchMultiAnalysisTrendMeasureData(api, {
          multiAnalysisMeasureType: args.measureType,
          pointIds,
          measureNames,
          range: args.range,
        }),
        args.cellFilters,
      );

      return normalizeMultiAnalysisTrendSeriesDates(
        buildMultiAnalysisTrendSeries(response, args.points, measureOptions, {
          unitLabelFormatter: unitCode => formatMessage({ id: `enums.units.${unitCode}` }),
          pointDirectionLabelFormatter: direction => formatMessage({ id: `enums.pointDirection.${direction}` }),
        }),
        args.dbTz,
      );
    },
    { success: false, error: false },
  );
  const loadTrendSeriesRef = useRef(loadTrendSeries);

  useEffect(() => {
    loadTrendSeriesRef.current = loadTrendSeries;
  }, [loadTrendSeries]);

  useEffect(() => {
    const requestRange = normalizeTrendRangeForRequest(range, dbTz);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || !points.length || !requestRange || !measureType) {
      setSeries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    void loadTrendSeriesRef
      .current(undefined, { points, range: requestRange, measureType, cellFilters, dbTz })
      .then(nextSeries => {
        if (requestIdRef.current === requestId) {
          setSeries(nextSeries ?? []);
        }
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          setSeries([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [cellFilters, dbTz, enabled, measureType, points, range]);

  return { series, isLoading };
};
